import { ERROR_MESSAGES } from './constants';
import {
  PulseAPILogData,
  PulseConfig,
  PulseGenericLogger,
  PulseLogData,
} from './interfaces';
import Pulse from './pulse';
import WebLogger from './pulseLogger';

let pulse: Pulse;

/**
 * Function to setup Pulse beacon.
 *
 * This will add beacon(piece of Javascript code) to HEAD section of HTML
 * Place this as high up as possible at the point of entry in your app.
 *
 * It should be available before any logger functions are called
 *
 * @see - https://dx.walmart.com/documents/product/Pulse/1-Adding-beacon-js-to-Your-Site-agc5wj8thl
 *
 * @param initialSetupDetails
 *
 * @deprecated Use the new WebLogger API
 */
function initializePulseBeacon(initialSetupDetails: PulseConfig): void {
  try {
    pulse = new Pulse({ ...initialSetupDetails });
  } catch (error) {
    console.error(
      `${ERROR_MESSAGES.INITIALIZATION} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
    );
    console.error(error);
  }
}

/**
 * Function to send API call logs to Pulse. Should ideally be called from Axios or related function's interceptors
 *
 * @param apiLogs
 *
 * @deprecated Use the new WebLogger API
 */
function pulseAPILogger(apiLogs: PulseAPILogData): void {
  try {
    pulse.sendAPIDataToPulse({ ...apiLogs });
  } catch (error) {
    console.error(
      `${ERROR_MESSAGES.API_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
    );
    console.error(error);
  }
}

/**
 * Function to send all application exceptions to Pulse. Should ideally be called from your app's error boundary code
 *
 * Log level - fatal
 *
 * @param exceptionLogs
 *
 * @deprecated Use the new WebLogger API
 */
function pulseExceptionLogger(exceptionLogs: PulseLogData): void {
  try {
    pulse.sendExceptionDataToPulse({ ...exceptionLogs });
  } catch (error) {
    console.error(
      `${ERROR_MESSAGES.EXCEPTION_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
    );
    console.error(error);
  }
}

/**
 * Function to send page view/ routing to Pulse
 *
 * Log level - info
 *
 * @param pageViewLogs
 *
 * @deprecated Use the new WebLogger API
 */
function pulsePageViewLogger(pageViewLogs: PulseLogData): void {
  try {
    pulse.sendPageViewDataToPulse({ ...pageViewLogs });
  } catch (error) {
    console.error(
      `${ERROR_MESSAGES.PAGEVIEW_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
    );
    console.error(error);
  }
}

/**
 * @deprecated Use the new WebLogger API
 */
const pulseLogger = {
  /**
   * Generic info logger
   *
   * Log level - info
   *
   * @param infoLogs
   *
   * @deprecated Use the new WebLogger API
   */
  info: (infoLogs: PulseGenericLogger) => {
    try {
      pulse.infoLogger({ ...infoLogs });
    } catch (error) {
      console.error(
        `${ERROR_MESSAGES.INFO_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
      );
      console.error(error);
    }
  },

  /**
   * Generic warning logger
   *
   * Log level - warning
   *
   * @param warnLogs
   *
   * @deprecated Use the new WebLogger API
   */
  warn: (warnLogs: PulseGenericLogger) => {
    try {
      pulse.warnLogger({ ...warnLogs });
    } catch (error) {
      console.error(
        `${ERROR_MESSAGES.WARN_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
      );
      console.error(error);
    }
  },

  /**
   * Generic error logger
   *
   * Log level - error
   *
   * @param errorLogs
   *
   * @deprecated Use the new WebLogger API
   */
  error: (errorLogs: PulseGenericLogger) => {
    try {
      pulse.errorLogger({ ...errorLogs });
    } catch (error) {
      console.error(
        `${ERROR_MESSAGES.ERROR_LOG} ${ERROR_MESSAGES.ENSURE_INITIALIZATION_DONE} ${ERROR_MESSAGES.ENABLE_DEBUG}`,
      );
      console.error(error);
    }
  },
};

export {
  initializePulseBeacon,
  pulseAPILogger,
  pulseExceptionLogger,
  pulsePageViewLogger,
  pulseLogger,
  WebLogger,
};
